import { DisconnectOutlined, DownloadOutlined, LockFilled, SyncOutlined } from '@ant-design/icons'
import { useMetadata } from '@api/useMetadata'
import { useSites } from '@api/useSites'
import { useMasterMetadata } from '@api/useMetadata/useMasterMetadata'
import { useRatebooks } from '@api/useFreight/useFreight'
import { InNetworkTreeSelectEditor } from '@components/_SiteWide/Grid/CellEditors/InNetworkTerminalsTreeSelect'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { useCreateConfig } from '@components/Admin/Site/createConfig'
import { RowExpanded } from '@components/Admin/Site/Grid/Detail/RowExpanded'
import { FreightRegionsDrawer } from '@components/Admin/Site/FreightRegionsDrawer'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { BBDTag, GraviButton, GraviGrid, Texto, useThemeContext, useApi } from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import api, { getAPIURL } from '@utils/api'
import { getStoreNumberComparator, gridViewManager } from '@utils/grid'
import { Drawer, Popover, Tooltip, message } from 'antd'
import { useQueryClient } from '@tanstack/react-query'

import queryString from 'query-string'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const STORAGE_KEY = 'sites'

export function Sites() {
  const defaultFilter = { status: 'open' }
  const queryClient = useQueryClient()

  const storeNumber = queryString.parse(window.location.search).store_number
  if (storeNumber) defaultFilter.store_number = storeNumber
  const [filters, setFilters] = useState(defaultFilter)
  const [showFreightRegionsDrawer, setShowFreightRegionsDrawer] = useState(false)
  const { data: storeMetadata } = useMetadata('store')
  const { data: marketMetadata } = useMetadata('market')
  const { isLoading: configLoading, siteCreateConfig } = useCreateConfig()

  const { useSiteOverviewQuery, createSiteMutation, updateSiteMutation } = useSites()
  const { data: sites, isFetching, refetch } = useSiteOverviewQuery(filters)
  const { mutateAsync } = createSiteMutation()
  const { mutateAsync: updateAsync } = updateSiteMutation()
  const { currentTheme } = useThemeContext()

  const createSite = (newSite) => {
    newSite.market = storeMetadata.sectors.find((market) =>
      market.options.some((sector) => sector === newSite.sector)
    )?.name

    newSite.lat = parseFloat(newSite.lat)
    newSite.lon = parseFloat(newSite.lon)
    newSite.status_override = 'open'
    newSite.status = 'open'
    const cp = storeMetadata.counterparties.find((cp) => cp.name === newSite.counterparty)
    newSite.counterparty_name = cp.name
    newSite.counterparty_id = cp.id

    const prom = mutateAsync(newSite)
    const res = new Promise((resolve) => {
      resolve({ data: prom })
    })
    return res
  }

  const updateSite = async (editedSite) => {
    editedSite.in_network_terminals = editedSite.in_network_terminal_names
    const selectedTerminals = editedSite.in_network_terminals
    const marketTerminals = editedSite.market_in_network_terminal_names
    const sectorTerminals = editedSite.sector_in_network_terminal_names
    if (
      selectedTerminals.length === sectorTerminals.length &&
      selectedTerminals?.every((terminal) => sectorTerminals.includes(terminal))
    ) {
      editedSite.in_network_terminal_names = []
    }
    if (
      selectedTerminals.length === marketTerminals.length &&
      selectedTerminals?.every((terminal) => marketTerminals.includes(terminal))
    ) {
      editedSite.in_network_terminal_names = []
    }

    // Ensure freight region name is updated if freight region id changes
    if (editedSite.freight_region_id) {
      const freightRegions = storeMetadata?.freight_regions

      if (freightRegions) {
        const freightRegion = freightRegions.find((region) => region.id === editedSite.freight_region_id)
        if (freightRegion) {
          editedSite.freight_region_name = freightRegion.name
        } else {
          // Try reverse lookup by name if ID lookup fails
          if (editedSite.freight_region_name) {
            const regionByName = freightRegions.find((region) => region.name === editedSite.freight_region_name)
            if (regionByName) {
              editedSite.freight_region_id = regionByName.id
            }
          }
        }
      }
    } else if (editedSite.freight_region_id === null) {
      editedSite.freight_region_name = null
    }

    return updateAsync(editedSite)
  }

  const { scopes } = useSecurityContext()
  const canEdit = scopes.admin?.site_write
  const { updateSetting } = useUserSettings()
  const columnDefs = useMemo(() => {
    return getColumnDefs(storeMetadata, marketMetadata)
  }, [storeMetadata, marketMetadata])
  const getRowId = useCallback((row) => row.data?.id, [])
  const gridApiRef = useRef()
  useEffect(() => {
    gridApiRef?.current?.forEachNode((node) => {
      node.setExpanded(false)
    })
  }, [currentTheme])
  const agPropOverrides = useMemo(() => ({ getRowId }), [])

  const handleFreightRegionsDrawerClose = useCallback(() => {
    setShowFreightRegionsDrawer(false)
  }, [])

  return (
    <>
      <Drawer
        title='Manage Freight Regions'
        placement='right'
        width='500'
        closable
        onClose={handleFreightRegionsDrawerClose}
        open={showFreightRegionsDrawer}
        rootClassName='freight-regions-drawer'
        destroyOnClose
      >
        <FreightRegionsDrawer closeDrawer={handleFreightRegionsDrawerClose} sites={sites?.rows || []} />
      </Drawer>
      <GraviGrid
        controlBarProps={{
          serverParams: sites?.params,
          filters,
          setFilters,
          title: 'Site Management',
          actionButtons: (
            <>
              <UploadDownloadButtons
                excelOptions={[
                  {
                    title: 'Download Sites',
                    downloadEP: api.downloadSites,
                    fileName: 'Sites.xlsx',
                  },
                  {
                    title: 'Upload Sites',
                    uploadEP: canEdit && api.uploadSites,
                  },
                  {
                    title: 'Download Unavailable Hours',
                    downloadEP: api.downloadUnavailableHours,
                    fileName: 'Unavailable Hours.xlsx',
                  },
                  {
                    title: 'Upload Unavailable Hours',
                    uploadEP: canEdit && api.uploadUnavailableHours,
                  },
                  {
                    title: 'Download No Forecast Hours',
                    downloadEP: api.downloadNoForecastHours,
                    fileName: 'No Forecast Hours.xlsx',
                  },
                  {
                    title: 'Upload No Forecast Hours',
                    uploadEP: canEdit && api.uploadNoForecastHours,
                  },
                ]}
                refreshCallback={refetch}
              />
              {canEdit && (
                <>
                  <GraviButton
                    appearance='outlined'
                    theme2
                    buttonText='Manage Freight Regions'
                    onClick={() => setShowFreightRegionsDrawer(!showFreightRegionsDrawer)}
                  />
                </>
              )}
            </>
          ),
        }}
        createEP={canEdit && createSite}
        createConfig={siteCreateConfig}
        updateEP={canEdit && updateSite}
        createSelectOptions={{ ...marketMetadata, ...storeMetadata }}
        loading={isFetching || configLoading}
        agPropOverrides={agPropOverrides}
        columnDefs={columnDefs}
        detailRowAutoHeight
        detailCellRenderer={RowExpanded}
        masterDetail
        storageKey={STORAGE_KEY}
        rowData={sites?.rows}
        detailCellRendererParams={{
          storeMetadata,
        }}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        externalRef={gridApiRef}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
    </>
  )
}

function getFilterOptionsForTerminals(marketMetadata, params) {
  const flat_list = []
  Object.keys(marketMetadata?.supply_zones)?.forEach((zone) => {
    marketMetadata?.supply_zones[zone].forEach((terminal) => {
      flat_list.push(terminal)
    })
  })
  return params.success(flat_list)
}

function getFilterOptionsForSupplyZones(marketMetadata, params) {
  const flat_list = []
  Object.keys(marketMetadata?.supply_zones)?.forEach((zone) => {
    if (zone === 'No Supply Zone') return
    flat_list.push(zone)
  })
  return params.success(flat_list)
}

function customValueFormatter(params) {
  return params.value
}

const getColumnDefs = (storeMetadata, marketMetadata) => [
  {
    editable: false,
    cellRenderer: 'agGroupCellRenderer',
    field: 'id',
    valueFormatter: () => '',
    headerName: '',
    maxWidth: 50,
    filter: false,
  },
  {
    editable: false,
    field: 'monitoring_strategy',
    headerName: 'Monitor Strategy',
    filter: true,
    minWidth: 60,
    flex: 0,
    cellRenderer: ({ value }) => {
      if (value === 'N/A') return null
      return (
        <div className='mr-2'>
          <Tooltip title={value}>
            <BBDTag warning={value.toLowerCase() === 'unmonitored'} theme2={value.toLowerCase() !== 'unmonitored'}>
              {value.toLowerCase() === 'unmonitored' ? <DisconnectOutlined /> : <DownloadOutlined />}
            </BBDTag>
          </Tooltip>
        </div>
      )
    },
    valueGetter: (params) => (params?.data?.monitoring_strategy ? params?.data?.monitoring_strategy : 'N/A'),
  },
  {
    editable: false,
    headerName: 'Status',
    filter: true,
    field: 'status_override',
    valueGetter: (params) => {
      if (params?.data?.status_override === 'comp. hold' && params?.data?.credit_hold)
        return ['Comp. Hold', 'Credit Hold']
      if (params?.data?.credit_hold) return 'Credit Hold'
      if (params?.data?.status_override === 'comp. hold') return 'Comp. Hold'
      return params?.data?.status_override
    },
    cellRenderer: ({ data }) => {
      if (!data.credit_hold && !data?.status_override) return ''
      return (
        <BBDTag
          style={{ minWidth: 'fit-content' }}
          textTransform='capitalize'
          theme2={data.status_override?.toLowerCase() === 'open'}
          error={data.credit_hold || data.status_override === 'comp. hold'}
        >
          {data.credit_hold && <LockFilled className='mr-2' />}
          {data?.credit_hold ? 'Credit Hold' : (data?.status_override ?? 'N/A')}
        </BBDTag>
      )
    },
    maxWidth: 170,
  },
  {
    editable: false,
    field: 'store_number',
    headerName: 'Site',
    comparator: (valueA, valueB) => {
      if (!valueA || !valueB) return 0
      return valueA.localeCompare(valueB, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    },
    cellRenderer: (params) => <b>{params?.value}</b>,
    filter: true,
    minWidth: 60,
  },
  {
    editable: false,
    field: 'name',
    minWidth: 100,
  },
  {
    editable: false,
    headerName: 'Counterparty',
    field: 'counterparty_name',
    filter: true,
    minWidth: 200,
    valueGetter: (params) => (params?.data?.counterparty_name ? params?.data?.counterparty_name : 'N/A'),
    cellRenderer: ({ value }) => (value === 'N/A' ? null : value),
  },
  {
    editable: false,
    field: 'address',
    minWidth: 250,
  },
  {
    editable: false,
    field: 'city',
    minWidth: 120,
  },
  {
    editable: false,
    field: 'state',
    minWidth: 60,
  },
  {
    headerName: 'Postal Code',
    editable: false,
    field: 'postal_code',
    filter: true,
    comparator: getStoreNumberComparator('postal_code'),
  },

  {
    editable: false,
    field: 'sector',
    minWidth: 200,
    valueFormatter: ({ value }) => value?.toUpperCase(),
  },
  {
    editable: false,
    field: 'notes',
    minWidth: 120,
    initialHide: true,
    valueGetter: (params) => {
      if (!params?.data?.notes) return 'N/A'
      if (params?.data?.notes?.length > 15) return `${params?.data?.notes?.slice(0, 15)}...`
      return params?.data?.notes
    },
    cellRenderer: ({ value }) => {
      if (!value || value === 'N/A') return null
      return value
    },
  },
  {
    editable: true,
    minWidth: 225,
    headerName: 'In-Network Terminals',
    field: 'in_network_terminal_names',
    cellEditor: InNetworkTreeSelectEditor,
    cellEditorParams: {
      supply_zones: marketMetadata?.supply_zones,
    },
    cellRenderer: (params) => {
      if (params?.data?.in_network_terminal_names?.length) {
        const terminals = params.data.only_in_network_terminal_names
        const supplyZones = params.data.only_in_network_supply_zone_names
        const hasTerminals = terminals?.length > 0
        const hasSupplyZones = supplyZones?.length > 0

        const content = (
          <>
            {hasSupplyZones && <Texto weight='bold'>Supply Zones:</Texto>}
            {supplyZones?.map((supplyZone) => (
              <Texto key={supplyZone}>{supplyZone}</Texto>
            ))}
            {hasTerminals && hasSupplyZones && <br />}
            {hasTerminals && <Texto weight='bold'>Terminals:</Texto>}
            {terminals?.map((terminal) => (
              <Texto key={terminal}>{terminal}</Texto>
            ))}
          </>
        )

        return (
          <Popover content={content} placement='topLeft'>
            <GraviButton buttonText={params?.data?.in_network_terminal_names.length} />
          </Popover>
        )
      }
      return params.data?.sector_in_network_terminal_names?.length ? ['Inherits from Sector'] : ['Inherits from Market']
    },
    valueFormatter: (params) => {
      if (params.node?.group) {
        const node = params?.node?.childrenAfterGroup[0]
        const terminals = node?.data?.only_in_network_terminal_names.join(', ')
        const supplyZones = node?.data?.only_in_network_supply_zone_names.join(', ')

        if (!terminals && !supplyZones) {
          return 'No In Network Terminals'
        }

        if (!terminals) {
          return supplyZones
        }

        if (!supplyZones) {
          return terminals
        }

        return `${supplyZones} --- ${terminals}`
      }

      return params.value
    },
    filter: 'agMultiColumnFilter',
    filterParams: {
      filters: [
        {
          filter: 'agSetColumnFilter',
          display: 'subMenu',
          title: 'Filter By Supply Zone',
          filterParams: {
            field: 'number',
            buttons: ['reset'],
            values: (params) => getFilterOptionsForSupplyZones(marketMetadata, params),
            valueGetter: (params) => {
              return params?.data?.only_in_network_supply_zone_names
            },
            valueFormatter: customValueFormatter,
          },
        },
        {
          filter: 'agSetColumnFilter',
          display: 'subMenu',
          title: 'Filter By Terminal',
          filterParams: {
            showTooltips: true,
            field: 'type',
            values: (params) => getFilterOptionsForTerminals(marketMetadata, params),
            valueGetter: ({ data }) => {
              return data?.in_network_terminal_names
            },
            valueFormatter: customValueFormatter,
            buttons: ['reset'],
          },
        },
      ],
    },
  },
  {
    field: 'geofence',
    editable: false,
    headerTooltip:
      'Use a tool like https://geojson.io to define a polygon and copy paste the list of coordinates into the loader. The coordinates are in the longitude, latitude format. Will use the default terminal radius if left blank.',
    valueFormatter: ({ value }) => (value && Array.isArray(value) && value.length > 0 ? 'Polygon' : null),
  },
  {
    editable: false,
    field: 'freight_region_name',
    headerName: 'Freight Region',
    filter: true,
    minWidth: 150,
    valueGetter: (params) => (params?.data?.freight_region_name ? params?.data?.freight_region_name : 'N/A'),
    cellRenderer: ({ value }) => (value === 'N/A' ? null : value),
  },
]
